/**
 * Blackout poetry: lay a page of text out on a fixed monospace grid, let the writer keep
 * some words, and work out which stretches of the page to cover with a bar.
 *
 * The grid is the point. Every word has a row and a column, so the same layout drives the
 * on-screen preview, the printout and the SVG and PNG downloads, and nothing has to
 * measure rendered text (so it is safe to compute during server rendering).
 */

export interface Token {
  /** Position in the text, counting every word. Stable until the text changes. */
  index: number;
  text: string;
  /** Which paragraph (blank-line separated block) the word came from. */
  paragraph: number;
}

export interface Placed {
  index: number;
  row: number;
  col: number;
  len: number;
}

export interface Layout {
  items: Placed[];
  /** Rows used, counting the blank rows between paragraphs. */
  rows: number;
}

export interface Run {
  row: number;
  col: number;
  len: number;
}

export interface Tokenized {
  tokens: Token[];
  /** True when the text had more words than `maxWords`. */
  truncated: boolean;
  total: number;
}

const RE_LINE_ENDINGS = /\r\n?/g;
const RE_PARAGRAPH_BREAK = /\n\s*\n/;
const RE_WHITESPACE = /\s+/;
const RE_NOT_LETTER = /[^\p{L}\p{N}']/gu;

/** Columns on the page grid. 60 monospace columns fill a US Letter line at 15 px type. */
export const PAGE_COLUMNS = 60;

/** Words the tool will lay out: about two printed pages of prose. */
export const MAX_WORDS = 600;

function chunk(word: string, size: number): string[] {
  const pieces: string[] = [];
  for (let at = 0; at < word.length; at += size) {
    pieces.push(word.slice(at, at + size));
  }
  return pieces;
}

/** Split pasted text into words, remembering paragraph breaks. Very long words are cut. */
export function tokenize(
  text: string,
  maxWords: number = MAX_WORDS,
  columns: number = PAGE_COLUMNS
): Tokenized {
  const tokens: Token[] = [];
  let total = 0;
  const paragraphs = text
    .replace(RE_LINE_ENDINGS, "\n")
    .split(RE_PARAGRAPH_BREAK);
  for (const [paragraph, block] of paragraphs.entries()) {
    for (const word of block.trim().split(RE_WHITESPACE)) {
      if (word === "") {
        continue;
      }
      for (const piece of chunk(word, columns)) {
        total += 1;
        if (tokens.length < maxWords) {
          tokens.push({ index: tokens.length, text: piece, paragraph });
        }
      }
    }
  }
  return { tokens, truncated: total > tokens.length, total };
}

/** Wrap the words onto the grid, leaving one blank row between paragraphs. */
export function layoutWords(
  tokens: readonly Token[],
  columns: number = PAGE_COLUMNS
): Layout {
  const items: Placed[] = [];
  let row = 0;
  let col = 0;
  let paragraph = tokens[0]?.paragraph ?? 0;
  for (const token of tokens) {
    const len = token.text.length;
    if (token.paragraph !== paragraph) {
      row += 2;
      col = 0;
      paragraph = token.paragraph;
    } else if (col > 0 && col + len > columns) {
      row += 1;
      col = 0;
    }
    items.push({ index: token.index, row, col, len });
    col += len + 1;
  }
  return { items, rows: tokens.length === 0 ? 0 : row + 1 };
}

/** The stretches to cover: one bar per unbroken run of unkept words on a row. */
export function blackoutRuns(
  items: readonly Placed[],
  kept: ReadonlySet<number>
): Run[] {
  const runs: Run[] = [];
  let current: Run | null = null;
  for (const item of items) {
    if (kept.has(item.index)) {
      current = null;
      continue;
    }
    if (current && current.row === item.row) {
      current.len = item.col + item.len - current.col;
    } else {
      current = { row: item.row, col: item.col, len: item.len };
      runs.push(current);
    }
  }
  return runs;
}

/** The kept words, one string per page row that has any: the poem as it reads on the page. */
export function keptLines(
  tokens: readonly Token[],
  items: readonly Placed[],
  kept: ReadonlySet<number>
): string[] {
  const lines: string[] = [];
  let lastRow = -1;
  for (const item of items) {
    if (!kept.has(item.index)) {
      continue;
    }
    const word = tokens[item.index].text;
    if (item.row === lastRow) {
      lines[lines.length - 1] += ` ${word}`;
    } else {
      lines.push(word);
      lastRow = item.row;
    }
  }
  return lines;
}

function normalise(word: string): string {
  return word.toLowerCase().replace(RE_NOT_LETTER, "");
}

/**
 * Pick words by reading forward: each wanted word is the next token (after the last one
 * picked) that matches it, ignoring case and punctuation. Words that never appear are
 * skipped. Used for the sample poem.
 */
export function selectInOrder(
  tokens: readonly Token[],
  wanted: readonly string[]
): Set<number> {
  const picked = new Set<number>();
  let at = 0;
  for (const word of wanted) {
    const target = normalise(word);
    for (let i = at; i < tokens.length; i += 1) {
      if (normalise(tokens[i].text) === target) {
        picked.add(tokens[i].index);
        at = i + 1;
        break;
      }
    }
  }
  return picked;
}

/** Every index from `a` to `b`, in either order (for shift-click ranges). */
export function indexRange(a: number, b: number): number[] {
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  return Array.from({ length: high - low + 1 }, (_, i) => low + i);
}
